import { create } from 'zustand';
import { persist, devtools } from 'zustand/middleware';
import type { User } from '@welynk/types';
import { useUIStore } from '@/store/ui-store';

interface AuthStore {
  user: User | null;
  setUser: (user: User | null) => void;
  clearUser: () => void;
  isAuthenticated: () => boolean;
  hasRole: (role: string | string[]) => boolean;
}

/**
 * Auth Store (Zustand with Persistence)
 *
 * Purpose: Manage authenticated user state
 *
 * Why Zustand?
 * - Persists user across page refreshes (better UX)
 * - No need to refetch user on every mount
 * - Can be accessed outside React components
 * - Simple API with minimal boilerplate
 *
 * Why Persisted?
 * - User stays logged in across browser sessions
 * - Reduces API calls on app initialization
 * - Faster app startup (no loading state for user data)
 *
 * Note: This stores USER DATA, not tokens.
 * Tokens are managed by the API layer (api-interceptors.ts)
 */
export const useAuthStore = create<AuthStore>()(
  devtools(
    persist(
      (set, get) => ({
        user: null,

        setUser: (user) => {
          // A different person signing in on this browser must not inherit the
          // previous one's selected college or company workspace.
          const previous = get().user;
          if (previous && user && previous.id !== user.id) {
            useUIStore.getState().clearWorkspaceContext();
          }
          set({ user });
        },

        clearUser: () => {
          useUIStore.getState().clearWorkspaceContext();
          set({ user: null });
        },

        isAuthenticated: () => get().user !== null,

        hasRole: (role) => {
          const user = get().user;
          if (!user) return false;

          if (Array.isArray(role)) {
            return role.includes(user.role);
          }
          return user.role === role;
        },
      }),
      {
        name: 'auth-store',
      }
    ),
    { name: 'Auth Store' }
  )
);
